import {
  sortHighToLowIcon,
  sortLowToHighIcon,
  sortNewToOldIcon,
  sortOldToNewIcon,
} from '@/shared/ui/assets';
import React from 'react';
import { SORT_INBOX_KEYS } from '@/entities/inbox';
import type { SortInboxProps } from './sortInbox.types';

/**
 * Picks the arrow icon that shows the inbox list's current sort direction. Memoizes on sortValue and maps the SORT_INBOX_KEYS new-to-old, old-to-new, high-to-low and low-to-high cases to their icons, defaulting to the new-to-old icon.
 *
 * @param sortValue - The active sort option.
 * @returns An object holding the resolved sort direction icon.
 */
const useSortInbox = ({ sortValue }: Pick<SortInboxProps, 'sortValue'>) => {
  /**
   * Memoized sort icon asset chosen from the active sort value. Switches over the new-to-old, old-to-new, high-to-low and low-to-high sort keys and falls back to the new-to-old icon.
   *
   * @returns The imported icon asset for the active sort order.
   */
  const displayIcon = React.useMemo(() => {
    switch (sortValue) {
      case SORT_INBOX_KEYS.NEW_TO_OLD:
        return sortNewToOldIcon;
      case SORT_INBOX_KEYS.OLD_TO_NEW:
        return sortOldToNewIcon;
      case SORT_INBOX_KEYS.HIGH_TO_LOW:
        return sortHighToLowIcon;
      case SORT_INBOX_KEYS.LOW_TO_HIGH:
        return sortLowToHighIcon;
      default:
        return sortNewToOldIcon;
    }
  }, [sortValue]);

  return { displayIcon };
};

export { useSortInbox };
